import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import {
  BubbleChart as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  FileCopy as ContentCopyIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Send as SendIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import FlashAutoIcon from '@material-ui/icons/FlashAuto';
import toastUtils from '../../utils/toastUtils';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import useSettings from '../../hooks/useSettings';

const useStyles = makeStyles((theme) => ({
  styledFab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1000,
    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
    }
  },
  suggestionCard: {
    marginBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(1),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[4]
    }
  },
  toneChip: {
    fontWeight: 600,
    fontSize: '0.75rem'
  },
  formalChip: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2'
  },
  casualChip: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32'
  },
  empatheticChip: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00'
  },
  professionalChip: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4)
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4)
  },
  chipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonsContainer: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  feedbackContainer: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  configSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  }
}));

// Componentes modais simples para substituir StandardModalAdv/StandardModal
const SimpleModal = ({ open, onClose, title, subtitle, children, actions = [], loading = false }) => {
  const classes = useStyles();

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }}>
      <Card style={{
        maxWidth: 800,
        width: '90%',
        maxHeight: '90%',
        overflow: 'auto',
        margin: 16
      }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <PsychologyIcon style={{ marginRight: 8, color: '#2196f3' }} />
              <Box>
                <Typography variant="h6">{title}</Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">{subtitle}</Typography>
                )}
              </Box>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {loading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={40} />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Gerando sugestões...
              </Typography>
            </Box>
          )}

          {!loading && children}

          {actions.length > 0 && (
            <Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'outlined'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  startIcon={action.startIcon}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AISuggestionWidget = ({
  ticketId,
  onSuggestionSelect,
  aiSettings,
  open,
  onClose
}) => {
  const classes = useStyles();
  const { getAll: getAllSettings } = useSettings();
  const [settings, setSettings] = useState({});

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  // Estados para configurações locais
  const [localConfig, setLocalConfig] = useState({
    contextLength: (aiSettings && aiSettings.aiContextLength) || 20,
    maxSuggestions: (aiSettings && aiSettings.aiMaxSuggestions) || 3,
    temperature: (aiSettings && aiSettings.aiTemperature) || 0.7,
    maxTokens: (aiSettings && aiSettings.aiMaxTokens) || 1500,
    confidenceThreshold: (aiSettings && aiSettings.aiSuggestionConfidenceThreshold) || 0.7
  });

  // Carregar configurações
  useEffect(() => {
    let isMounted = true;
    
    const loadSettings = async () => {
      try {
        const settingsData = await getAllSettings();
        if (isMounted) {
          const settingsMap = {};
          settingsData.forEach(setting => {
            settingsMap[setting.key] = setting.value;
          });
          setSettings(settingsMap);
        }
      } catch (error) {
        console.warn('Erro ao carregar configurações:', error);
        if (isMounted) {
          setSettings({});
        }
      }
    };
    
    loadSettings();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removido getAllSettings das dependências para evitar loops

  useEffect(() => {
    loadLocalConfig();
  }, []);

  // Atualizar configurações locais quando as globais mudarem
  useEffect(() => {
    if (aiSettings) {
      setLocalConfig(prev => ({
        ...prev,
        contextLength: aiSettings.aiContextLength || prev.contextLength,
        maxSuggestions: aiSettings.aiMaxSuggestions || prev.maxSuggestions,
        temperature: aiSettings.aiTemperature || prev.temperature,
        maxTokens: aiSettings.aiMaxTokens || prev.maxTokens,
        confidenceThreshold: aiSettings.aiSuggestionConfidenceThreshold || prev.confidenceThreshold
      }));
    }
  }, [aiSettings]);

  const loadLocalConfig = () => {
    const savedConfig = localStorage.getItem('aiSuggestionConfig');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setLocalConfig(prev => ({ ...prev, ...parsedConfig }));
      } catch (error) {
        console.error('Erro ao carregar configurações locais:', error);
      }
    }
  };

  const saveLocalConfig = () => {
    localStorage.setItem('aiSuggestionConfig', JSON.stringify(localConfig));
    setConfigOpen(false);
    toastUtils.success('Configurações pessoais salvas com sucesso!', 'Configurações');
  };

  // Gerar sugestões
  const generateSuggestions = async () => {
    if (!aiSettings || !aiSettings.openAiKey) {
      toastUtils.error('Configurações de IA não encontradas. Configure nas Configurações Gerais.', 'Erro');
      return;
    }

    setLoading(true);
    try {
      const requestConfig = {
        model: aiSettings.openaiModel || 'gpt-4o',
        maxSuggestions: localConfig.maxSuggestions,
        contextLength: localConfig.contextLength,
        temperature: localConfig.temperature,
        maxTokens: localConfig.maxTokens,
        confidenceThreshold: localConfig.confidenceThreshold,
        language: 'pt'
      };

      const response = await api.post(`/ai-suggestions/tickets/${ticketId}/suggestions`, requestConfig);

      if (response.data && response.data.suggestions) {
        setSuggestions(response.data.suggestions);
        toastUtils.success('Sugestões geradas com sucesso!', 'IA');
      } else {
        toastUtils.warning('Nenhuma sugestão foi gerada para este ticket.', 'IA');
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Erro ao gerar sugestões:', error);
      if (error.response && error.response.status === 401) {
        toastUtils.error('Chave da OpenAI inválida. Entre em contato com o administrador.', 'Erro');
      } else {
        const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Erro ao gerar sugestões';
        toastUtils.error(errorMessage, 'Erro');
      }
    } finally {
      setLoading(false);
    }
  };

  // Usar sugestão
  const handleUseSuggestion = async (suggestion) => {
    try {
      // Registrar que a sugestão foi usada (se feedback estiver habilitado)
      if (aiSettings && aiSettings.aiEnableFeedback) {
        await api.post(`/ai-suggestions/suggestions/${suggestion.id}/feedback`, {
          used: true,
          helpful: true
        });
      }

      // Chamar callback para inserir texto no campo de resposta
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion.content);
      }

      toastUtils.success('Sugestão aplicada!', 'IA');
      if (onClose) onClose();
    } catch (error) {
      console.error('Erro ao registrar feedback:', error);
      // Ainda permite usar a sugestão mesmo se o feedback falhar
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion.content);
      }
      if (onClose) onClose();
    }
  };

  // Copiar sugestão
  const handleCopySuggestion = async (suggestion) => {
    try {
      await navigator.clipboard.writeText(suggestion.content);
      toastUtils.success('Sugestão copiada para a área de transferência!', 'Copiado');
    } catch (error) {
      console.error('Erro ao copiar:', error);
      toastUtils.error('Erro ao copiar sugestão', 'Erro');
    }
  };

  // Dar feedback
  const handleFeedback = async (suggestionId, helpful) => {
    if (!aiSettings?.aiEnableFeedback) {
      return;
    }

    try {
      await api.post(`/ai-suggestions/suggestions/${suggestionId}/feedback`, {
        used: false,
        helpful
      });

      toastUtils.success('Obrigado pelo feedback!', 'Feedback');
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
    }
  };

  // Atualizar configuração local
  const handleConfigChange = (key, value) => {
    setLocalConfig(prev => ({ ...prev, [key]: value }));
  };

  // Obter classe do chip de tom
  const getToneChipClass = (tone) => {
    switch(tone) {
      case 'formal': return classes.formalChip;
      case 'casual': return classes.casualChip;
      case 'empathetic': return classes.empatheticChip;
      case 'professional': return classes.professionalChip;
      default: return classes.professionalChip;
    }
  };

  // Renderizar chip de categoria
  const renderCategoryChip = (category) => {
    const categoryLabels = {
      information: 'Informação',
      support: 'Suporte',
      sales: 'Vendas',
      follow_up: 'Acompanhamento',
      escalation: 'Escalação'
    };

    const categoryColors = {
      information: 'primary',
      support: 'primary',
      sales: 'secondary',
      follow_up: 'default',
      escalation: 'secondary'
    };

    return (
      <Chip
        label={categoryLabels[category] || category}
        color={categoryColors[category] || 'default'}
        size="small"
        variant="outlined"
      />
    );
  };

  const renderMainContent = () => {
    if (suggestions.length === 0) {
      return (
        <Box className={classes.emptyState}>
          <PsychologyIcon style={{ fontSize: 64, color: '#999', marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            Nenhuma sugestão disponível
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Clique em "Gerar Sugestões" para obter recomendações de resposta baseadas no contexto da conversa.
          </Typography>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <FlashAutoIcon />}
            onClick={generateSuggestions}
            disabled={loading}
            size="large"
          >
            {loading ? 'Gerando...' : 'Gerar Sugestões'}
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        {suggestions.map((suggestion, index) => (
          <Card key={suggestion.id || index} className={classes.suggestionCard} elevation={1}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box className={classes.chipContainer}>
                  <Chip
                    label={suggestion.tone || 'professional'}
                    size="small"
                    className={`${classes.toneChip} ${getToneChipClass(suggestion.tone)}`}
                  />
                  {suggestion.category && renderCategoryChip(suggestion.category)}
                  <Chip
                    label={`${Math.round((suggestion.confidence || 0.7) * 100)}%`}
                    size="small"
                    color={(suggestion.confidence || 0.7) >= localConfig.confidenceThreshold ? "primary" : "secondary"}
                    variant="outlined"
                  />
                </Box>
              </Box>

              <Typography variant="body1" paragraph>
                {suggestion.content || suggestion.text}
              </Typography>

              {suggestion.reasoning && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="caption" color="textSecondary">
                      Explicação da sugestão
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="textSecondary">
                      {suggestion.reasoning}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box className={classes.actionsContainer}>
                <Box className={classes.buttonsContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SendIcon />}
                    onClick={() => handleUseSuggestion(suggestion)}
                    size="small"
                  >
                    Usar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => handleCopySuggestion(suggestion)}
                    size="small"
                  >
                    Copiar
                  </Button>
                </Box>

                {aiSettings?.aiEnableFeedback && (
                  <Box className={classes.feedbackContainer}>
                    <Tooltip title="Útil">
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(suggestion.id, true)}
                        style={{ color: '#4caf50' }}
                      >
                        <ThumbUpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Não útil">
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(suggestion.id, false)}
                        style={{ color: '#f44336' }}
                      >
                        <ThumbDownIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const renderConfigContent = () => (
    <Box className={classes.configSection}>
      <Typography variant="body2" color="primary" gutterBottom>
        <strong>Configuração Global:</strong> {aiSettings?.openaiModel || 'N/A'} configurado para toda a empresa.
        Aqui você pode ajustar suas preferências pessoais de comportamento da IA.
      </Typography>

      <TextField
        label="Máximo de Sugestões"
        type="number"
        value={localConfig.maxSuggestions}
        onChange={(e) => handleConfigChange('maxSuggestions', parseInt(e.target.value))}
        inputProps={{ min: 1, max: 5 }}
        fullWidth
        helperText={`Padrão da empresa: ${aiSettings?.aiMaxSuggestions || 3}`}
      />

      <TextField
        label="Tamanho do Contexto"
        type="number"
        value={localConfig.contextLength}
        onChange={(e) => handleConfigChange('contextLength', parseInt(e.target.value))}
        inputProps={{ min: 5, max: 50 }}
        fullWidth
        helperText={`Número de mensagens para análise. Padrão: ${aiSettings?.aiContextLength || 20}`}
      />

      <TextField
        label="Criatividade (Temperature)"
        type="number"
        value={localConfig.temperature}
        onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
        inputProps={{ min: 0.1, max: 1.0, step: 0.1 }}
        fullWidth
        helperText={`0.1 = Conservador, 1.0 = Criativo. Padrão: ${aiSettings?.aiTemperature || 0.7}`}
      />

      <TextField
        label="Máximo de Tokens"
        type="number"
        value={localConfig.maxTokens}
        onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
        inputProps={{ min: 500, max: 4000 }}
        fullWidth
        helperText={`Tamanho máximo da resposta. Padrão: ${aiSettings?.aiMaxTokens || 1500}`}
      />

      <TextField
        label="Limite de Confiança (%)"
        type="number"
        value={Math.round(localConfig.confidenceThreshold * 100)}
        onChange={(e) => handleConfigChange('confidenceThreshold', parseInt(e.target.value) / 100)}
        inputProps={{ min: 10, max: 100, step: 5 }}
        fullWidth
        helperText={`Mínimo de confiança para mostrar sugestões. Padrão: ${Math.round((aiSettings?.aiSuggestionConfidenceThreshold || 0.7) * 100)}%`}
      />

      <Typography variant="body2" color="primary" style={{ marginTop: 16 }}>
        <strong><span role="img" aria-label="lâmpada">💡</span> Dica:</strong> Estas configurações são salvas localmente no seu navegador e
        não afetam outros usuários. Use configurações mais conservadoras para respostas mais precisas
        ou mais criativas para respostas mais variadas.
      </Typography>
    </Box>
  );

  // Verificar se IA está configurada
  if (!aiSettings?.openAiKey) {
    return (
      <SimpleModal
        open={open}
        onClose={onClose}
        title="IA Não Configurada"
        subtitle="Configure a chave da OpenAI para utilizar as sugestões de resposta"
        actions={[
          {
            label: 'Fechar',
            onClick: onClose,
            variant: 'outlined'
          },
          {
            label: 'Ir para Configurações',
            onClick: () => window.location.href = '/settings',
            variant: 'contained',
            startIcon: <SettingsIcon />
          }
        ]}
      >
        <Box p={2}>
          <Typography variant="body1" color="error">
            As configurações de Inteligência Artificial não foram encontradas.
            Configure a chave da OpenAI nas Configurações Gerais para utilizar as sugestões de resposta.
          </Typography>
        </Box>
      </SimpleModal>
    );
  }

  const getMainModalActions = () => [
    {
      label: loading ? 'Gerando...' : 'Gerar Novas Sugestões',
      onClick: generateSuggestions,
      disabled: loading,
      variant: 'outlined'
    },
    {
      label: 'Fechar',
      onClick: onClose,
      variant: 'contained'
    }
  ];

  const getConfigModalActions = () => [
    {
      label: 'Cancelar',
      onClick: () => setConfigOpen(false),
      variant: 'outlined'
    },
    {
      label: 'Salvar Preferências',
      onClick: saveLocalConfig,
      variant: 'contained'
    }
  ];

  return (
    <>
      {/* Modal Principal */}
      <SimpleModal
        open={open}
        onClose={onClose}
        title="Sugestões de Resposta IA"
        subtitle={`Configuração: ${aiSettings?.openaiModel || 'N/A'} • Máx. ${localConfig.maxSuggestions} sugestões • Contexto: ${localConfig.contextLength} mensagens`}
        loading={loading}
        actions={getMainModalActions()}
      >
        {/* Informações sobre configuração global */}
        <Box p={1} mb={2} style={{ backgroundColor: '#e3f2fd', borderRadius: 4 }}>
          <Typography variant="body2" color="primary">
            <strong>Configuração Ativa:</strong> {aiSettings?.openaiModel || 'N/A'} •
            Máx. {localConfig.maxSuggestions} sugestões •
            Contexto: {localConfig.contextLength} mensagens
            <IconButton
              onClick={() => setConfigOpen(true)}
              size="small"
              style={{ marginLeft: 8 }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Typography>
        </Box>

        {renderMainContent()}
      </SimpleModal>

      {/* Modal de Configurações Pessoais */}
      <SimpleModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        title="Configurações Pessoais de IA"
        subtitle="Ajuste suas preferências pessoais de comportamento da IA"
        actions={getConfigModalActions()}
      >
        {renderConfigContent()}
      </SimpleModal>
    </>
  );
};

export default AISuggestionWidget;